import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  DeckCheck,
  Nullable,
  TournamentPlayer,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

export const DECK_CHECK_FIELDS = gql`
  fragment DeckCheckFields on DeckCheck {
    id
    tournamentId
    matchId
    playerId
    judgeId
    round
    result
    notes
    penaltyId
    startedAt
    completedAt
    player {
      id
      name
      lastName
    }
    judge {
      id
      name
      lastName
    }
    match {
      id
      round
      tableNumber
    }
  }
`;

export type StartDeckCheckInput = {
  tournamentId: number;
  matchId: number;
  playerId: number;
};

export const startDeckCheck = async (
  user: UserWithToken,
  input: StartDeckCheckInput
) =>
  getGraphQLQueryClient(user).request<WithData<DeckCheck>>(
    gql`
      ${DECK_CHECK_FIELDS}
      mutation startDeckCheck($input: StartDeckCheckInput!) {
        data: startDeckCheck(input: $input) {
          ...DeckCheckFields
        }
      }
    `,
    { input }
  );

export type StartDeckChecksInput = {
  tournamentId: number;
  matchId: number;
  playerIds: number[];
};

/** Uno o los dos jugadores de la mesa a la vez. */
export const startDeckChecks = async (
  user: UserWithToken,
  input: StartDeckChecksInput
) =>
  getGraphQLQueryClient(user).request<WithData<DeckCheck[]>>(
    gql`
      ${DECK_CHECK_FIELDS}
      mutation startDeckChecks($input: StartDeckChecksInput!) {
        data: startDeckChecks(input: $input) {
          ...DeckCheckFields
        }
      }
    `,
    { input }
  );

export const completeDeckCheck = async (
  user: UserWithToken,
  deckCheckId: number,
  result: string,
  notes?: string | null,
  applyPenalty?: boolean
) =>
  getGraphQLQueryClient(user).request<WithData<DeckCheck>>(
    gql`
      ${DECK_CHECK_FIELDS}
      mutation completeDeckCheck(
        $deckCheckId: Int!
        $result: DeckCheckResult!
        $notes: String
        $applyPenalty: Boolean
      ) {
        data: completeDeckCheck(
          deckCheckId: $deckCheckId
          result: $result
          notes: $notes
          applyPenalty: $applyPenalty
        ) {
          ...DeckCheckFields
        }
      }
    `,
    {
      deckCheckId,
      result,
      notes: notes ?? null,
      applyPenalty: applyPenalty ?? false,
    }
  );

export const cancelDeckCheck = async (
  user: UserWithToken,
  deckCheckId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation cancelDeckCheck($deckCheckId: Int!) {
        data: cancelDeckCheck(deckCheckId: $deckCheckId)
      }
    `,
    { deckCheckId }
  );

export const getTournamentDeckChecks = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round?: number
) =>
  getGraphQLQueryClient(user).request<WithData<DeckCheck[]>>(
    gql`
      ${DECK_CHECK_FIELDS}
      query tournamentDeckChecks($tournamentId: Int!, $round: Int) {
        data: tournamentDeckChecks(tournamentId: $tournamentId, round: $round) {
          ...DeckCheckFields
        }
      }
    `,
    { tournamentId, round: round ?? null }
  );

export const getUncheckedPlayers = async (
  user: UserWithToken,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer[]>>(
    gql`
      query uncheckedPlayers($tournamentId: Int!) {
        data: uncheckedPlayers(tournamentId: $tournamentId) {
          id
          status
          # El id del jugador sale de la relación: TournamentPlayer no expone
          # un userId plano.
          user {
            id
            name
            lastName
          }
        }
      }
    `,
    { tournamentId }
  );
